<template>
  <v-container
    v-if="locale && webpage"
    :class="containerClass"
    class="my-8"
  >
    <grid-layout
      v-model:layout="webpage.layout[size]"
      :auto-size="true"
      :col-num="36"
      :is-draggable="false"
      :is-resizable="false"
      :row-height="8"
    >
      <grid-item
        v-for="item in sortedLayoutItems"
        :key="item.i"
        :h="item.h"
        :i="item.i"
        :w="item.w"
        :x="item.x"
        :y="item.y"
      >
        <PageElement
          v-model="elements[item.i]"
          mode="view"
        />
      </grid-item>
    </grid-layout>
  </v-container>
</template>

<script setup>
import { GridLayout, GridItem } from 'grid-layout-plus';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import PageElement from '@/shared/components/PageElement.vue';
import useApi from '@/shared/composables/useApi';

const { api: Api } = useApi();
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();
const display = useDisplay();
const store = useStore();

const $bct = inject('translator');

let elements = ref(null);
let lang = ref(null);
let webpage = ref(null);

let containerClass = computed(() => {
  if (size.value === 'lg') return 'mxw-1200';
  if (size.value === 'md') return 'mxw-800';
  return 'mxw-440';
});

let size = computed(() => {
  if (display.lgAndUp.value) return 'lg';
  if (display.mdAndUp.value) return 'md';
  return 'sm';
});
// Sort needed for tab order to match visual order; this does top/bottom/left/right hierarchy.
let sortedLayoutItems = computed(() => {
  const layout = webpage.value?.layout?.[size.value];
  if (!layout?.length) return [];

  return [...layout].sort((left, right) => {
    if (left.y !== right.y) return left.y - right.y;
    if (left.x !== right.x) return left.x - right.x;

    return 0;
  });
});

watch(
  () => route.params.webpageId,
  () => identifyUser(),
  { immediate: true },
);

function load() {
  const requestedWebpageId = route.params.webpageId;

  Api.publicApi.organization.webpage.get(
    requestedWebpageId,
    (response) => {
      if (requestedWebpageId !== route.params.webpageId) return;

      webpage.value = response.data;
      lang.value = route.query.lang || locale.value || store.state.profile?.default_locale;
      const title = $bct([store.state.config.title, webpage.value.name].join(' - '));
      document.title = title;
      const pageElements = {};
      webpage.value.elements.forEach((element) => (pageElements[element.i] = element));
      elements.value = pageElements;
    },
    async (error) => {
      if (requestedWebpageId !== route.params.webpageId) return;

      if (error?.response?.status === 404) {
        await router.replace({ name: 'Dashboard' });
        return;
      }

      throw error;
    },
  );
}

function identifyUser() {
  store.dispatch('identify', {
    success() {
      load();
    },
    failure() {
      load();
    },
  });
}
</script>

<style scoped>
.v-btn__content {
  white-space: normal !important;
}
</style>
